import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import {
  Shield, Lock, Fingerprint, Eye, EyeOff, Loader2, Users, Trophy,
  TrendingUp, Heart, CheckCircle, Clock, AlertTriangle, BarChart3,
  FileText, Star, Zap, ChevronRight, RefreshCw, X, Check, Target, ArrowLeft, Send, Copy, Download
} from 'lucide-react';
import TeenOceanProfile from '../components/ocean/TeenOceanProfile';
import ParentCelebrationAlerts from '../components/dashboard/ParentCelebrationAlerts';
import TeenChallengeView from '../components/dashboard/TeenChallengeView';
import ParentFamilyVault from '../components/parent/ParentFamilyVault';
import ParentVerificationCard from '../components/parent/ParentVerificationCard';
import ParentCharterModal from '../components/parent/ParentCharterModal';
import DashboardCover from '../components/DashboardCover';

// ============== AUTH GATE COMPONENT ==============
const ParentAuthGate = ({ onAuthenticated }) => {
  const { user } = useAuth();
  const [method, setMethod] = useState(null); // 'pin' | 'biometric'
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);

  useEffect(() => {
    // Check WebAuthn / biometric availability
    if (window.PublicKeyCredential) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setBiometricAvailable(available))
        .catch(() => setBiometricAvailable(false));
    }
  }, []);

  const handlePinSubmit = async () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    setAuthenticating(true);
    setError('');
    try {
      const res = await apiClient.request('/api/family/parent/verify-pin', {
        method: 'POST',
        body: JSON.stringify({ pin })
      });
      if (res.verified) {
        onAuthenticated();
      } else {
        setError('Incorrect PIN. Please try again.');
      }
    } catch (err) {
      // For now, allow access on first use (PIN not yet set)
      if (err.message?.includes('not set') || err.message?.includes('404') || err.message?.includes('Not Found')) {
        onAuthenticated();
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setAuthenticating(false);
    }
  };

  const handleBiometric = async () => {
    setAuthenticating(true);
    setError('');
    try {
      const publicKeyOptions = {
        challenge: new Uint8Array(32),
        rp: { name: 'HiBFF Parent Dashboard' },
        user: {
          id: new TextEncoder().encode(user?.user_id || 'parent'),
          name: user?.email || 'parents@hibff.com',
          displayName: user?.name || 'Parent'
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required'
        },
        timeout: 60000
      };

      // Try to authenticate with biometric
      await navigator.credentials.create({ publicKey: publicKeyOptions });
      onAuthenticated();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometric authentication was cancelled.');
      } else {
        setError('Biometric not available. Please use PIN.');
      }
    } finally {
      setAuthenticating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="w-full max-w-md mx-4" data-testid="parent-auth-gate">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Parent Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Verify your identity to access</p>
          </div>

          {!method ? (
            <div className="space-y-3">
              <button
                onClick={() => setMethod('pin')}
                data-testid="auth-pin-btn"
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Lock size={24} className="text-indigo-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Enter PIN</p>
                  <p className="text-xs text-gray-500">Use your 4-digit parent PIN</p>
                </div>
                <ChevronRight size={20} className="ml-auto text-gray-400" />
              </button>

              {biometricAvailable && (
                <button
                  onClick={handleBiometric}
                  disabled={authenticating}
                  data-testid="auth-biometric-btn"
                  className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Fingerprint size={24} className="text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Fingerprint / Face ID</p>
                    <p className="text-xs text-gray-500">Use biometric authentication</p>
                  </div>
                  {authenticating ? <Loader2 size={20} className="ml-auto animate-spin text-purple-500" /> : <ChevronRight size={20} className="ml-auto text-gray-400" />}
                </button>
              )}

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <AlertTriangle size={16} /> {error}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 sm:p-10" data-testid="pin-entry-card">
              <button onClick={() => { setMethod(null); setPin(''); setError(''); }} className="text-sm text-gray-500 hover:text-gray-700 mb-6 inline-flex items-center gap-1">&larr; Back</button>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Parent PIN</h2>
              <div className="relative mb-6">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter PIN"
                  data-testid="pin-input"
                  className="w-full px-4 py-4 pr-12 text-center text-2xl tracking-[0.5em] border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
                />
                <button onClick={() => setShowPin(!showPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {error && (
                <div className="mb-5 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <AlertTriangle size={16} /> {error}
                </div>
              )}
              <button
                onClick={handlePinSubmit}
                disabled={authenticating || pin.length < 4}
                data-testid="pin-submit-btn"
                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {authenticating ? <Loader2 size={20} className="animate-spin" /> : <><Lock size={18} /> Verify</>}
              </button>
              <p className="text-xs text-gray-400 text-center mt-6 mb-1">First time? Your PIN will be set on first entry.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ============== PARENT DASHBOARD CARDS ==============

const TeenOverviewCard = ({ teen, onViewDetail }) => (
  <div className="card" data-testid={`teen-card-${teen.teen_id}`}>
    <div className="card-header">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
          {teen.name?.charAt(0) || 'T'}
        </div>
        <div>
          <h3 className="card-title text-base">{teen.name}</h3>
          <p className="text-xs text-gray-500">Active member</p>
        </div>
      </div>
      {teen.pending_verifications > 0 && (
        <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
          {teen.pending_verifications} pending
        </span>
      )}
    </div>

    {/* Real-World Progress Banner */}
    {teen.real_world_challenges > 0 && (
      <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl" data-testid="teen-real-world-progress">
        <div className="flex items-center gap-1.5 mb-2">
          <Zap size={14} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-800 uppercase tracking-wider">Real-World Progress</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-green-700">{teen.real_world_challenges}</p>
            <p className="text-[10px] text-green-600">Total Actions</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-700">{teen.real_world_challenges_this_week || 0}</p>
            <p className="text-[10px] text-green-600">This Week</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-700">+{teen.real_world_xp || 0}</p>
            <p className="text-[10px] text-green-600">XP Earned</p>
          </div>
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-purple-50 rounded-lg text-center">
        <Trophy size={18} className="mx-auto text-purple-500 mb-1" />
        <p className="text-lg font-bold text-gray-900">{teen.badges_earned}</p>
        <p className="text-[10px] text-gray-500">Badges</p>
      </div>
      <div className="p-3 bg-orange-50 rounded-lg text-center">
        <Zap size={18} className="mx-auto text-orange-500 mb-1" />
        <p className="text-lg font-bold text-gray-900">{teen.current_streak}</p>
        <p className="text-[10px] text-gray-500">Day Streak</p>
        {teen.current_streak === 0 && (
          <p className="text-[9px] text-orange-400 mt-0.5">Starts with first real-world action</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 rounded-lg text-center">
        <TrendingUp size={18} className="mx-auto text-blue-500 mb-1" />
        <p className="text-lg font-bold text-gray-900">{teen.practice_sessions_this_week || teen.activities_this_week}</p>
        <p className="text-[10px] text-gray-500">Sessions/wk</p>
      </div>
      <div className="p-3 bg-red-50 rounded-lg text-center">
        <Heart size={18} className="mx-auto text-red-500 mb-1" />
        <p className="text-lg font-bold text-gray-900">{teen.hearts_balance}</p>
        <p className="text-[10px] text-gray-500">Hearts</p>
      </div>
    </div>
    {teen.average_mood !== null && teen.average_mood !== undefined && (
      <div className="mt-3 p-3 bg-green-50 rounded-lg">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Mood Score</span>
          <span className="font-bold text-green-700">{teen.average_mood}%</span>
        </div>
        <div className="w-full bg-green-200 rounded-full h-1.5 mt-1.5">
          <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${Math.min(teen.average_mood, 100)}%` }} />
        </div>
      </div>
    )}
    <button
      onClick={() => onViewDetail(teen)}
      className="w-full mt-3 py-2 text-sm font-semibold text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors flex items-center justify-center gap-1"
      data-testid={`view-detail-${teen.teen_id}`}
    >
      View Full Report <ChevronRight size={14} />
    </button>
  </div>
);

// ============== TEEN DETAIL PANEL ==============
const TeenDetailPanel = ({ teen, onBack }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request(`/api/family/parent/report/${teen.teen_id}?period=monthly`)
      .then(data => setReport(data))
      .catch(err => console.error('Report load failed:', err))
      .finally(() => setLoading(false));
  }, [teen.teen_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div data-testid={`teen-detail-${teen.teen_id}`}>
      {/* Back Button */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft size={16} /> Back to overview
      </button>

      {/* Teen Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
          {teen.name?.charAt(0) || 'T'}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{teen.name}&apos;s Progress</h2>
          <p className="text-sm text-gray-500">Monthly Report — {report?.period_start} to {report?.period_end}</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl text-center">
          <Target size={20} className="mx-auto text-green-600 mb-1" />
          <p className="text-2xl font-bold text-green-700">{report?.real_world?.challenges_completed || 0}</p>
          <p className="text-xs text-green-600">Real-World Actions</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl text-center">
          <Trophy size={20} className="mx-auto text-purple-600 mb-1" />
          <p className="text-2xl font-bold text-purple-700">{report?.badges?.total_earned || 0}</p>
          <p className="text-xs text-purple-600">Badges Earned</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl text-center">
          <TrendingUp size={20} className="mx-auto text-blue-600 mb-1" />
          <p className="text-2xl font-bold text-blue-700">{report?.engagement?.days_active || 0}</p>
          <p className="text-xs text-blue-600">Active Days</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl text-center">
          <Heart size={20} className="mx-auto text-amber-600 mb-1" />
          <p className="text-2xl font-bold text-amber-700">{report?.mood?.average || 50}%</p>
          <p className="text-xs text-amber-600">Mood Average</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Teen Challenge View */}
        <div className="md:col-span-2">
          <TeenChallengeView teenId={teen.teen_id} />
        </div>

        {/* Real-World Actions */}
        <div className="p-5 bg-white rounded-xl border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Target size={18} className="text-green-500" /> Recent Real-World Actions
          </h3>
          {report?.real_world?.recent_actions?.length > 0 ? (
            <div className="space-y-2">
              {report.real_world.recent_actions.map((a, i) => (
                <div key={`${a.action_label}-${a.timestamp || i}`} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-700">{a.action_label}</span>
                  <span className="text-xs font-semibold text-green-600">+{a.xp_earned} XP</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">No real-world actions logged yet</p>
          )}
        </div>

        {/* Skills Progress */}
        <div className="p-5 bg-white rounded-xl border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <BarChart3 size={18} className="text-blue-500" /> Skill Areas
          </h3>
          <div className="space-y-3">
            {report?.skills?.map((skill) => (
              <div key={skill.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">{skill.name}</span>
                  <span className="text-xs font-semibold text-gray-900">{skill.score}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      skill.score >= 60 ? 'bg-green-500' : skill.score >= 30 ? 'bg-yellow-500' : 'bg-gray-300'
                    }`}
                    style={{ width: `${Math.max(skill.score, 2)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Badges */}
        <div className="p-5 bg-white rounded-xl border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Trophy size={18} className="text-purple-500" /> Recent Badges
          </h3>
          {report?.badges?.recent_badges?.length > 0 ? (
            <div className="space-y-2">
              {report.badges.recent_badges.map((b, i) => (
                <div key={b.id || `${b.name}-${i}`} className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
                  <span className="text-xl">{b.icon}</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{b.name}</p>
                    <p className="text-xs text-gray-500">Earned this period</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">No badges earned this period</p>
          )}
        </div>

        {/* Highlights & Growth */}
        <div className="space-y-4">
          {report?.highlights?.length > 0 && (
            <div className="p-5 bg-green-50 rounded-xl border border-green-200">
              <h3 className="font-bold text-green-800 mb-2 text-sm">Highlights</h3>
              <ul className="space-y-1.5">
                {report.highlights.map((h, i) => (
                  <li key={`hl-${i}-${String(h).slice(0, 30)}`} className="text-xs text-green-700 flex items-start gap-1.5">
                    <Star size={12} className="text-green-500 flex-shrink-0 mt-0.5" /> {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {report?.growth_areas?.length > 0 && (
            <div className="p-5 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="font-bold text-amber-800 mb-2 text-sm">Areas for Growth</h3>
              <ul className="space-y-1.5">
                {report.growth_areas.map((a, i) => (
                  <li key={`ga-${i}-${String(a).slice(0, 30)}`} className="text-xs text-amber-700 flex items-start gap-1.5">
                    <TrendingUp size={12} className="text-amber-500 flex-shrink-0 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Privacy Reminder */}
      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
        <Shield size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800">
          This report shows activity frequency, real-world progress, and mood trends — never conversation content. Private conversations are never shared with parents.
        </p>
      </div>
    </div>
  );
};

const PendingVerificationsCard = ({ verifications, onVerify }) => (
  <div className="card" data-testid="pending-verifications-card">
    <div className="card-header">
      <h3 className="card-title">Pending Badge Verifications</h3>
      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">{verifications.length}</span>
    </div>
    {verifications.length === 0 ? (
      <div className="text-center py-6 text-gray-400">
        <CheckCircle size={32} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No pending verifications</p>
      </div>
    ) : (
      <div className="space-y-3">
        {verifications.map((v, i) => (
          <div key={v.verification_id || v.badge_id || `v-${v.badge_name}-${i}`} className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{v.badge_icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{v.badge_name}</p>
                <p className="text-xs text-gray-500">by {v.teen_name}</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-2">{v.requirement}</p>
            {v.submission_notes && <p className="text-xs text-blue-600 mb-2 italic">Note: &quot;{v.submission_notes}&quot;</p>}
            <div className="flex gap-2">
              <button
                onClick={() => onVerify(v.badge_id, v.teen_id, true)}
                className="flex-1 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Check size={14} /> Approve
              </button>
              <button
                onClick={() => onVerify(v.badge_id, v.teen_id, false)}
                className="flex-1 py-1.5 bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-300 flex items-center justify-center gap-1"
              >
                <X size={14} /> Return
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReportCard = ({ teen, report, onRefresh }) => {
  const [loading, setLoading] = useState(!report);
  const [reportData, setReportData] = useState(report);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await apiClient.request(`/api/family/parent/report/${teen.teen_id}?period=monthly`);
      // Treat "empty" responses (no activities at all) as no-data so the
      // cardVisibility gate hides the tile instead of rendering a stub.
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        setLoadFailed(true);
      } else {
        setReportData(data);
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [teen.teen_id]);

  useEffect(() => {
    if (!reportData && !loadFailed) loadReport();
  }, [reportData, loadFailed, loadReport]);

  // Sprint 208A — kill the infinite spinner. Three terminal states now:
  //   • loading       → small inline pulse, never the full big spinner card
  //   • loadFailed    → clean placeholder tile (matches Quick Actions rhythm)
  //   • reportData    → render the rich report
  if (loadFailed) {
    return (
      <div className="card" data-testid={`report-card-empty-${teen.teen_id}`}>
        <div className="card-header">
          <h3 className="card-title text-base">Monthly Report: {teen.name}</h3>
        </div>
        <div className="text-sm text-gray-500 py-2 leading-relaxed">
          <p className="font-medium text-gray-700 mb-1">Just getting started</p>
          <p>No activity recorded for {teen.name} this period yet. As challenges and badges roll in, this card will fill with a full monthly summary.</p>
        </div>
      </div>
    );
  }

  if (loading || !reportData) {
    return (
      <div className="card" data-testid={`report-card-${teen.teen_id}`}>
        <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Loading monthly report for {teen.name}…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid={`report-card-${teen.teen_id}`}>
      <div className="card-header">
        <h3 className="card-title text-base">Monthly Report: {teen.name}</h3>
        <button onClick={loadReport} className="p-1.5 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Overall Score */}
      <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Overall Score</p>
            <p className="text-3xl font-bold text-indigo-700">{reportData.overall?.score || 0}%</p>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
            reportData.overall?.rating === 'excellent' ? 'bg-green-100 text-green-700' :
            reportData.overall?.rating === 'good' ? 'bg-blue-100 text-blue-700' :
            reportData.overall?.rating === 'satisfactory' ? 'bg-yellow-100 text-yellow-700' :
            'bg-purple-100 text-purple-700'
          }`}>
            {reportData.overall?.score <= 15 ? 'Just getting started' :
             reportData.overall?.rating === 'needs_improvement' ? 'Early days — keep going' :
             reportData.overall?.rating?.replace('_', ' ')}
          </div>
        </div>
      </div>

      {/* Skills */}
      {reportData.skills?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Skill Areas</h4>
          <div className="space-y-2">
            {reportData.skills.map((skill) => (
              <div key={skill.name}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="text-gray-600">{skill.name}</span>
                  <span className="font-semibold text-gray-900">{skill.score}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${
                    skill.score >= 60 ? 'bg-green-500' : skill.score >= 30 ? 'bg-yellow-500' : 'bg-gray-300'
                  }`} style={{ width: `${skill.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engagement */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <div className="p-2 bg-gray-50 rounded-lg text-center">
          <p className="text-lg font-bold text-gray-900">{reportData.engagement?.activities_logged || 0}</p>
          <p className="text-[10px] text-gray-500">Activities</p>
        </div>
        <div className="p-2 bg-gray-50 rounded-lg text-center">
          <p className="text-lg font-bold text-gray-900">{reportData.engagement?.days_active || 0}</p>
          <p className="text-[10px] text-gray-500">Active Days</p>
        </div>
        <div className="p-2 bg-gray-50 rounded-lg text-center">
          <p className="text-lg font-bold text-gray-900">{reportData.mood?.days_logged || 0}</p>
          <p className="text-[10px] text-gray-500">Mood Logs</p>
        </div>
      </div>

      {/* Highlights */}
      {reportData.highlights?.length > 0 && (
        <div className="p-3 bg-green-50 rounded-lg">
          <h4 className="text-xs font-semibold text-green-700 mb-1.5">Highlights</h4>
          <ul className="space-y-1">
            {reportData.highlights.map((h, i) => (
              <li key={`hl-${i}-${String(h).slice(0, 30)}`} className="text-xs text-green-800 flex items-start gap-1.5">
                <Star size={12} className="text-green-500 flex-shrink-0 mt-0.5" /> {h}
              </li>
            ))}
          </ul>
        </div>
      )}

      {reportData.growth_areas?.length > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg mt-2">
          <h4 className="text-xs font-semibold text-amber-700 mb-1.5">Areas for Growth</h4>
          <ul className="space-y-1">
            {reportData.growth_areas.map((a, i) => (
              <li key={`ga2-${i}-${String(a).slice(0, 30)}`} className="text-xs text-amber-800 flex items-start gap-1.5">
                <TrendingUp size={12} className="text-amber-500 flex-shrink-0 mt-0.5" /> {a}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== PROGRESS REPORTS CARD ==============
const ProgressReportsCard = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) { setLoading(false); return; }
    fetch(`${API_URL}/api/reports/parent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { reports: [] })
      .then(d => setReports(d.reports || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadReport = (r) => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    fetch(`${API_URL}/api/reports/parent/${r.year}/${r.month}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = r.filename; a.click();
        URL.revokeObjectURL(url);
      });
  };

  if (loading) return null;
  if (reports.length === 0) return null;

  return (
    <div className="card" data-testid="progress-reports-card">
      <div className="card-header">
        <h3 className="card-title text-base">Progress Reports</h3>
      </div>
      <div className="space-y-2">
        {reports.map(r => (
          <div key={r.report_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg" data-testid={`parent-report-${r.report_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F22' }}>
                <FileText size={14} style={{ color: '#F5831F' }} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{r.student_name} &mdash; {r.month_name}</p>
                <p className="text-[10px] text-gray-500">{r.sessions} sessions &middot; {r.challenges} challenges &middot; {r.badges_earned} badges</p>
              </div>
            </div>
            <button onClick={() => downloadReport(r)} className="px-2.5 py-1 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#F5831F' }} data-testid={`download-parent-report-${r.report_id}`}>
              PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== MAIN PARENT DASHBOARD ==============

const ParentDashboardMain = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [charterPdf, setCharterPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTeen, setSelectedTeen] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');

  // Sprint 208 Revised item #2 — ALIGNED handshake detection (moved to top
  // of component to satisfy react-hooks/rules-of-hooks; cannot live after
  // any early return).
  const [alignedFlash, setAlignedFlash] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/family/parent/charter-status');
        if (cancelled) return;
        const ch = data?.charter || {};
        const isAligned = !!data?.signed_by_parent && (
          ch.status === 'active' || ch.status === 'aligned' || !!ch.teen_signed_at
        );
        if (isAligned) {
          setAlignedFlash(true);
          setTimeout(() => setAlignedFlash(false), 2200);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [inviteCopied, setInviteCopied] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, verifyRes, pdfRes] = await Promise.all([
        apiClient.request('/api/family/parent/dashboard'),
        apiClient.request('/api/badges/pending-verification').catch(() => []),
        apiClient.request('/api/family/charter/my-pdf').catch(() => null),
      ]);
      setDashboardData(dashRes);
      setVerifications(Array.isArray(verifyRes) ? verifyRes : []);
      if (pdfRes?.charter_pdfs?.length > 0) {
        setCharterPdf(pdfRes.charter_pdfs[0]);
      }
    } catch (err) {
      console.error('Failed to load parent dashboard:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleVerify = async (badgeId, teenId, approved) => {
    try {
      await apiClient.request('/api/badges/verify', {
        method: 'POST',
        body: JSON.stringify({ badge_id: badgeId, teen_id: teenId, approved })
      });
      await fetchDashboard();
    } catch (err) {
      console.error('Failed to verify badge:', err);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  const teens = dashboardData?.teens || [];

  // Sprint 207Y — cardVisibility dictionary.
  // Each key gates a masonry tile; when its data is empty we set false so the
  // tile is *omitted entirely* and CSS-columns reflows the layout cleanly
  // instead of leaving an empty box with trailing whitespace.
  const cardVisibility = {
    teenOverview: teens.length > 0,
    familyVault: teens.length > 0,
    oceanProfile: teens.length > 0,
    pendingVerifications: verifications.length > 0,
    progressReports: teens.length > 0,
    perTeenReports: teens.length > 0,
    celebrationAlerts: teens.length > 0,
    quickActions: true,           // navigation hub — always shows
    privacySafety: true,          // trust messaging — always shows
    charterStatus: !!charterPdf,  // collapse the charter tile when no PDF exists
  };

  return (
    <DashboardLayout>
      <div data-testid="parent-dashboard">
        <ParentVerificationCard />
        {/* Sprint 211 — Roll-up dashboard cover (HiBFF logo + tagline + welcome).
            Exact mirror of the teen-side DashboardCover; click to reveal,
            pull-down tab to slide back down. */}
        <DashboardCover
          orgName="HiBFF"
          logoUrl="/hibff-logo.png"
          subtitle={`Welcome, ${user?.name?.split(' ')[0] || 'parent'} 👋`}
          accentColor="#F5831F"
        />

        {/* Sprint 208 Master item #1 — Auto-firing Commitment Charter modal.
            Mounts immediately on dashboard entry; self-hides when the parent
            has already signed (probed against /api/family/parent/charter-status). */}
        <ParentCharterModal parentUser={user} />

        {/* Parent Account Indicator */}
        <div className="mx-4 sm:mx-8 mt-2 mb-0 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between" data-testid="parent-account-banner">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-indigo-500" />
            <span className="text-xs font-medium text-indigo-800">Viewing as parent: {user?.name} ({user?.email})</span>
          </div>
          <span className="text-[10px] text-indigo-500">Read-only — no purchases or changes can be made from this view</span>
        </div>

        {/* Hero Section — Sprint 208 Revised: roll-up blind animation on mount */}
        <div className="hero-section hibff-welcome-blind" data-testid="parent-welcome-blind">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="hero-greeting" data-testid="parent-welcome-greeting">
                Welcome, {(user?.name || 'parent').split(' ')[0]}
              </h1>
              <p className="hero-subtext">
                {teens.length > 0
                  ? `Monitoring ${teens.length} teen${teens.length > 1 ? 's' : ''} on their journey`
                  : 'Set up a charter to start monitoring your teen\'s progress'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="quick-action-button quick-action-secondary"
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={() => navigate('/charter')}
                className={`quick-action-button quick-action-primary${alignedFlash ? ' hibff-aligned-glow' : ''}`}
                data-testid="parent-view-charter-btn"
              >
                <FileText size={18} />
                View Charter
              </button>
              {charterPdf && (
                <a
                  href={`${API_URL}${charterPdf.pdf_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="quick-action-button inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#F5831F] rounded-lg hover:bg-[#E07318] transition-colors"
                  data-testid="parent-dashboard-charter-pdf-btn"
                >
                  <Download size={18} />
                  Charter PDF
                </a>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          {teens.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
              <div className="p-3 bg-white/80 rounded-xl text-center backdrop-blur-sm">
                <Users size={20} className="mx-auto text-indigo-500 mb-1" />
                <p className="text-xl font-bold text-gray-900">{teens.length}</p>
                <p className="text-xs text-gray-500">Teens</p>
              </div>
              <div className="p-3 bg-white/80 rounded-xl text-center backdrop-blur-sm">
                <Trophy size={20} className="mx-auto text-purple-500 mb-1" />
                <p className="text-xl font-bold text-gray-900">{teens.reduce((s, t) => s + t.badges_earned, 0)}</p>
                <p className="text-xs text-gray-500">Badges Earned</p>
              </div>
              <div className="p-3 bg-white/80 rounded-xl text-center backdrop-blur-sm" data-testid="parent-real-world-stat">
                <Target size={20} className="mx-auto text-green-500 mb-1" />
                <p className="text-xl font-bold text-gray-900">{teens.reduce((s, t) => s + (t.real_world_challenges || 0), 0)}</p>
                <p className="text-xs text-gray-500">Real-World Actions</p>
              </div>
              <div className="p-3 bg-white/80 rounded-xl text-center backdrop-blur-sm">
                <Clock size={20} className="mx-auto text-orange-500 mb-1" />
                <p className="text-xl font-bold text-gray-900">{dashboardData?.pending_verifications || 0}</p>
                <p className="text-xs text-gray-500">Pending Reviews</p>
              </div>
              <div className="p-3 bg-white/80 rounded-xl text-center backdrop-blur-sm">
                <BarChart3 size={20} className="mx-auto text-green-500 mb-1" />
                <p className="text-xl font-bold text-gray-900">
                  {teens.reduce((s, t) => s + t.activities_this_week, 0)}
                </p>
                <p className="text-xs text-gray-500">Activities/wk</p>
              </div>
            </div>
          )}
        </div>

        {/* HiBFF Privacy Shield — Trust Messaging */}
        <div className="mx-4 sm:mx-8 mt-4 mb-2 p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl" data-testid="privacy-shield-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield size={24} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-indigo-900 mb-1 flex items-center gap-2">
                HiBFF Privacy Shield
                <span className="text-[10px] font-semibold px-2 py-0.5 bg-green-100 text-green-700 rounded-full uppercase">Active</span>
              </h3>
              <p className="text-sm text-indigo-800 mb-3">
                Your teen&apos;s personal information is protected at every level. Here&apos;s how:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>PII Redaction</strong> — Names, emails, and personal details are stripped before any AI model sees the conversation</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Conversation Privacy</strong> — You see activity and progress, never the actual conversation content</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Auto-Delete</strong> — All conversation data is automatically deleted after 90 days</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Safety Guardrails</strong> — 9 content domains are monitored and blocked in real-time, including self-harm and bullying</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Your Controls</strong> — Export or delete your teen&apos;s data anytime from Settings</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>COPPA & GDPR Compliant</strong> — Built for teen safety from the ground up</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/70 rounded-lg">
                  <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>SOC 2 Type II Ready</strong> — Enterprise-grade audit logging and access controls</span>
                </div>
              </div>
              <a href="/privacy" className="inline-block mt-3 text-xs text-indigo-600 hover:text-indigo-800 font-medium underline">
                Read our full Privacy Policy
              </a>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        {teens.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 rounded-2xl flex items-center justify-center">
              <Users size={40} className="text-indigo-500" />
            </div>
            {/* Check if user is a teen in a charter, not a parent */}
            {dashboardData?.is_teen_in_charter ? (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">This Dashboard is for Parents</h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm">
                  Your parent can view your progress here — including your real-world challenges, badges, and mood trends. Your conversations stay private.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  data-testid="back-to-dashboard-cta"
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center gap-2"
                >
                  Back to My Dashboard
                </button>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">No Teens Connected</h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm">
                  Invite your teen to join HiBFF, then create a charter agreement together.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => setShowInviteModal(true)}
                    data-testid="invite-teen-cta"
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center gap-2"
                  >
                    <Send size={18} /> Invite Your Teen
                  </button>
                  <button
                    onClick={() => navigate('/charter')}
                    data-testid="create-charter-cta"
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center gap-2"
                  >
                    <FileText size={18} /> Create Charter
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="dashboard-masonry" data-testid="parent-dashboard-masonry">
            {/* Sprint 207Y — Parent Dashboard uses CSS-columns masonry
                (`column-fill: balance; column-count: 1→2→3` responsive). Each
                tile is gated by `cardVisibility[key]` so empty data states
                disappear instead of rendering a broken empty box, forcing an
                instant column reflow. Every tile applies `break-inside: avoid`
                inline so no tile is ever split mid-card across columns. */}
            {selectedTeen ? (
              <div className="dashboard-masonry-item" style={{ breakInside: 'avoid', width: '100%' }}>
                <TeenDetailPanel teen={selectedTeen} onBack={() => setSelectedTeen(null)} />
              </div>
            ) : (
              <>
                {cardVisibility.teenOverview && teens.map((teen) => (
                  <div
                    key={`teen-${teen.teen_id}`}
                    className={`dashboard-masonry-item${alignedFlash ? ' hibff-aligned-glow' : ''}`}
                    style={{ breakInside: 'avoid' }}
                    data-testid={`parent-card-teen-overview-${teen.teen_id}`}
                  >
                    <TeenOverviewCard teen={teen} onViewDetail={setSelectedTeen} />
                  </div>
                ))}
                {cardVisibility.familyVault && (
                  <div
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid="parent-card-family-vault"
                  >
                    <ParentFamilyVault />
                  </div>
                )}
                {cardVisibility.oceanProfile && teens.map((teen) => (
                  <div
                    key={`ocean-${teen.teen_id}`}
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid={`parent-card-ocean-${teen.teen_id}`}
                  >
                    <TeenOceanProfile teenUserId={teen.teen_id} />
                  </div>
                ))}
                {cardVisibility.pendingVerifications && (
                  <div
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid="parent-card-pending-verifications"
                  >
                    <PendingVerificationsCard verifications={verifications} onVerify={handleVerify} />
                  </div>
                )}
                {cardVisibility.progressReports && (
                  <div
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid="parent-card-progress-reports"
                  >
                    <ProgressReportsCard />
                  </div>
                )}
                {cardVisibility.perTeenReports && teens.map((teen) => (
                  <div
                    key={`report-${teen.teen_id}`}
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid={`parent-card-report-${teen.teen_id}`}
                  >
                    <ReportCard teen={teen} report={null} />
                  </div>
                ))}
                {cardVisibility.celebrationAlerts && (
                  <div
                    className="dashboard-masonry-item"
                    style={{ breakInside: 'avoid' }}
                    data-testid="parent-card-celebrations"
                  >
                    <ParentCelebrationAlerts />
                  </div>
                )}
                {cardVisibility.quickActions && (
                <div
                  className="dashboard-masonry-item"
                  style={{ breakInside: 'avoid' }}
                  data-testid="parent-card-quick-actions"
                >
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title text-base">Quick Actions</h3>
                    </div>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/charter')}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <FileText size={20} className="text-purple-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">View Charter</p>
                      <p className="text-xs text-gray-500">Review the family agreement</p>
                    </div>
                    <ChevronRight size={16} className="ml-auto text-gray-400" />
                  </button>
                  <button
                    onClick={() => navigate('/badges')}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <Trophy size={20} className="text-amber-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Badge Catalog</p>
                      <p className="text-xs text-gray-500">View all 20 badges</p>
                    </div>
                    <ChevronRight size={16} className="ml-auto text-gray-400" />
                  </button>
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <BarChart3 size={20} className="text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Full Reports</p>
                      <p className="text-xs text-gray-500">Weekly & monthly progress</p>
                    </div>
                    <ChevronRight size={16} className="ml-auto text-gray-400" />
                  </button>
                </div>
              </div>
              </div>
              )}

              {/* Privacy Notice */}
              {cardVisibility.privacySafety && (
              <div
                className="dashboard-masonry-item"
                style={{ breakInside: 'avoid' }}
                data-testid="parent-card-privacy-safety"
              >
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-base">Privacy & Safety</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">Mood scores visible (not conversation details)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">Badge progress and achievements tracked</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">Activity frequency - not content</p>
                  </div>
                  <div className="flex items-start gap-2 mt-3 p-2.5 bg-blue-50 rounded-lg">
                    <Shield size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-800 text-xs">Private conversations and journal entries are never shared with parents.</p>
                  </div>
                </div>
              </div>
              </div>
              )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Invite Teen Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Invite Your Teen to HiBFF</h3>
            <p className="text-sm text-gray-600 mb-4">Copy the message below and send it to your teen via text, WhatsApp, or any messaging app you use. We don&apos;t message your teen directly.</p>

            {!inviteLink ? (
              <button
                onClick={async () => {
                  try {
                    const resp = await apiClient.request('/api/parent/invite-teen', {
                      method: 'POST', body: JSON.stringify({ teen_name: '' })
                    });
                    setInviteLink(resp.invite_link);
                    setInviteMessage(
                      `Hey, I've set you up on HiBFF, a training app to practise conversations and confidence (not social media, not therapy). It's private and just between us. You can check it out and join here: ${resp.invite_link}`
                    );
                  } catch (e) { console.error(e); }
                }}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700"
              >
                Generate Invite Link
              </button>
            ) : (
              <>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm h-32 resize-none mb-3"
                  data-testid="invite-message"
                />
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => { navigator.clipboard.writeText(inviteMessage); setInviteCopied('message'); setTimeout(() => setInviteCopied(''), 2000); }}
                    className="flex-1 py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 flex items-center justify-center gap-2 text-sm"
                  >
                    {inviteCopied === 'message' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Message</>}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(inviteLink); setInviteCopied('link'); setTimeout(() => setInviteCopied(''), 2000); }}
                    className="flex-1 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {inviteCopied === 'link' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Link Only</>}
                  </button>
                </div>
                <p className="text-xs text-gray-400 text-center">Paste this into your usual app (text, WhatsApp, Messenger, etc.). We don&apos;t message your teen directly.</p>
              </>
            )}

            <button onClick={() => { setShowInviteModal(false); setInviteLink(''); setInviteMessage(''); setInviteCopied(''); }}
              className="mt-3 w-full py-2 bg-gray-100 rounded-xl text-sm font-medium text-gray-600">Close</button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ============== EXPORTED COMPONENT WITH AUTH GATE ==============

const ParentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [isTeenUser, setIsTeenUser] = useState(null);
  const [checking, setChecking] = useState(true);

  // Check if this user is a teen in a charter before showing PIN gate
  useEffect(() => {
    apiClient.request('/api/family/charter')
      .then(charter => {
        if (charter && charter.teen_id === user?.user_id) {
          setIsTeenUser(true);
        } else {
          setIsTeenUser(false);
        }
      })
      .catch(() => setIsTeenUser(false))
      .finally(() => setChecking(false));
  }, [user]);

  if (checking) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  // Teen users see a friendly redirect message — no PIN required
  if (isTeenUser) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-4" data-testid="teen-parent-dash-message">
            <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 rounded-2xl flex items-center justify-center">
              <Shield size={40} className="text-indigo-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">This Dashboard is for Parents</h2>
            <p className="text-gray-500 mb-6 text-sm">
              Your parent can view your progress here — including your real-world challenges, badges, and mood trends. Your conversations stay private and are never shared.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center gap-2"
              data-testid="back-to-dashboard-cta"
            >
              Back to My Dashboard
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!authenticated) {
    // Sprint 211_FINAL — TOTP-auth parents (auth_mode === 'totp' OR
    // totp_setup_complete) bypass the legacy 4-digit PIN gate entirely.
    // Their JWT IS the second factor (they passed TOTP at /parent-login).
    if (user?.auth_mode === 'totp' || user?.totp_setup_complete || user?.role === 'parent') {
      return <ParentDashboardMain />;
    }
    return <ParentAuthGate onAuthenticated={() => setAuthenticated(true)} />;
  }

  return <ParentDashboardMain />;
};

export default ParentDashboard;
